import React, { useCallback, useMemo, useState } from "react";
import { Stack, Button, Alert, LoadingOverlay } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconCheck, IconTrash } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import { Employee } from "../../types/salon";
import {
  CommissionRecord,
  CreateCommissionInput,
} from "../../types/commission";
import { StaffTier, StaffTierCommissionDefault } from "../../types/staffTier";
import NewCommissionForm from "./NewCommissionForm";
import TierSyncModal from "./TierSyncModal";

interface StaffTierCommissionDefaultsProps {
  tier: StaffTier;
  canManage: boolean;
  canAssign: boolean;
  /** Close the containing modal (the container also refreshes on close). */
  onClose?: () => void;
}

/** Adapt the tier's commission template into the CommissionRecord shape the
 * shared NewCommissionForm consumes (audit/date fields are unused here). */
const tierDefaultToRecord = (
  cd: StaffTierCommissionDefault,
): CommissionRecord => ({
  id: cd.id,
  staff_id: "",
  service_amount: cd.service_amount,
  service_amount_type: cd.service_amount_type,
  product_amount: cd.product_amount,
  product_amount_type: cd.product_amount_type,
  calculation_method: cd.calculation_method,
  effective_start_date: "",
  num_services_cutoff: cd.num_services_cutoff,
  tiers: cd.tiers,
  created_dt: "",
  updated_dt: "",
  created_by: "",
  updated_by: "",
});

/**
 * Tier commission-defaults editor. Reuses the exact commission form from the
 * Staff Commissions settings page (NewCommissionForm) so the experience is
 * identical, minus the "pick staff" step and the effective-date field (a tier
 * template has no date — the date is set to "today" when applied to staff).
 * Saving the template then offers to apply it to the tier's members.
 */
const StaffTierCommissionDefaults: React.FC<
  StaffTierCommissionDefaultsProps
> = ({ tier, canManage, canAssign, onClose }) => {
  const {
    getTierCommissionDefault,
    upsertTierCommissionDefault,
    deleteTierCommissionDefault,
    applyTierCommissionDefaults,
  } = useApp();
  const { employees } = useSalon() as { employees: Employee[] | null };

  const [existing, setExisting] = useState<CommissionRecord | null>(null);
  const [exists, setExists] = useState(false);
  // Bumped after each load so NewCommissionForm remounts with the saved values
  // as its new baseline.
  const [formKey, setFormKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const notifySuccess = (message: string): void => {
    notifications.show({
      title: "Success",
      message,
      color: "var(--color-success)",
      icon: <IconCheck size={18} />,
    });
  };

  const members = useMemo(
    () =>
      (employees || [])
        .filter((e) => e.tier_id === tier.id)
        .map((e) => ({
          value: e.id,
          label: `${e.first_name} ${e.last_name}`.trim() || e.email,
        })),
    [employees, tier.id],
  );

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getTierCommissionDefault(tier.id);
      const cd = res.commission_default;
      setExisting(cd ? tierDefaultToRecord(cd) : null);
      setExists(!!cd);
      setFormKey((k) => k + 1);
    } catch (e) {
      console.error("Error loading tier commission default:", e);
      setError("Couldn't load the commission default.");
    } finally {
      setIsLoading(false);
    }
  }, [tier.id, getTierCommissionDefault]);

  useSalonScopedFetch(tier.id, load, { tag: "tier-commission-default" });

  const handleSubmit = async (input: CreateCommissionInput): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await upsertTierCommissionDefault(tier.id, {
        service_amount: input.service_amount ?? null,
        service_amount_type:
          input.service_amount != null
            ? (input.service_amount_type ?? null)
            : null,
        product_amount: input.product_amount ?? null,
        product_amount_type:
          input.product_amount != null
            ? (input.product_amount_type ?? null)
            : null,
        calculation_method: input.calculation_method,
        num_services_cutoff:
          input.calculation_method === "NUM_SERVICES"
            ? (input.num_services_cutoff ?? null)
            : null,
        // Carry the full tiered schedule for a TIERED template (backend
        // supports staff_tier_commission_tiers); omitted for other methods.
        tiers:
          input.calculation_method === "TIERED"
            ? (input.tiers ?? {})
            : undefined,
      });
      if (canAssign && members.length > 0) {
        // Tier has members — offer to apply the saved template to them. The
        // editor stays mounted behind the prompt; it closes + notifies once the
        // apply is confirmed or dismissed.
        setApplyOpen(true);
      } else {
        notifySuccess("Commission structure saved");
        onClose?.();
      }
    } catch (e) {
      console.error("Error saving tier commission default:", e);
      setError("Couldn't save the commission default. Check the values.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    setRemoving(true);
    setError(null);
    try {
      await deleteTierCommissionDefault(tier.id);
      notifySuccess("Commission structure removed");
      onClose?.();
    } catch (e) {
      console.error("Error deleting tier commission default:", e);
      setError("Couldn't remove the commission default.");
    } finally {
      setRemoving(false);
    }
  };

  const handleApply = async (staffIds: string[] | "all"): Promise<void> => {
    setApplying(true);
    try {
      const res = await applyTierCommissionDefaults(tier.id, {
        staff_ids: staffIds,
        acknowledge: true,
      });
      setApplyOpen(false);
      notifySuccess(res.message || "Commission structure applied");
      onClose?.();
    } catch (e) {
      console.error("Error applying commission default:", e);
      setApplyOpen(false);
      setError("Couldn't apply the commission default to staff.");
    } finally {
      setApplying(false);
    }
  };

  // Dismissing the apply prompt (only reachable after a successful save) means
  // "saved, but don't push to members" — close the editor and confirm the save.
  const dismissApply = (): void => {
    setApplyOpen(false);
    notifySuccess("Commission structure saved");
    onClose?.();
  };

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={isLoading} zIndex={5} />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {!isLoading && (
        <NewCommissionForm
          key={formKey}
          staffId=""
          existingCommission={existing}
          hideEffectiveDate
          disabled={!canManage}
          submitLabel="Save"
          hideCancel
          isLoading={saving}
          submitDisabled={removing}
          onSubmit={handleSubmit}
          onCancel={() => onClose?.()}
          leftSection={
            exists && canManage ? (
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconTrash size={16} />}
                onClick={remove}
                loading={removing}
                disabled={saving}
              >
                Remove default
              </Button>
            ) : undefined
          }
        />
      )}

      <TierSyncModal
        opened={applyOpen}
        onClose={dismissApply}
        tierName={tier.name}
        offerServiceReplace={false}
        offerCommission
        members={members}
        defaultChecked
        loading={applying}
        confirmLabel="Apply"
        onConfirm={({ staffIds }) => handleApply(staffIds)}
      />
    </Stack>
  );
};

export default StaffTierCommissionDefaults;
